import {
  ESCALA,
  escurecer,
  formatarValor,
  gradienteVertical,
  interpolarCores,
} from "../tema";
import { estiloRotulo, opcoesBase } from "./base";

/** Rampa padrão do modelo, aproximada do gráfico mensal de trigo. */
const RAMPA_PADRAO = ["#3FA9F5", "#7DD957", "#F0F000", "#FF8C00", "#7DD957", "#3DAA73"];

/**
 * Família 6 — série longa em colunas coloridas por rampa.
 *
 * Dezenas de colunas (meses de vários anos), cada uma com uma cor tirada
 * de uma rampa contínua, e rótulo apenas na primeira e na última. As
 * datas dos extremos aparecem nos cantos inferiores — o eixo inteiro não
 * caberia em 604px.
 */
const modelo = {
  id: "coluna-gradiente",
  label: "Colunas (série longa)",
  descricao: "Muitas colunas em rampa de cor, rótulo só nas pontas. Ex.: média mensal de 3 anos.",

  build(spec) {
    const base = opcoesBase(spec);
    const categorias = spec.dados?.categorias || [];
    const valores = spec.dados?.series?.[0]?.valores || [];
    const opcoesValor = spec.valores || {};
    const rampa = spec.extras?.rampa?.length ? spec.extras.rampa : RAMPA_PADRAO;
    const ultimo = valores.length - 1;

    return {
      ...base,
      chart: {
        ...base.chart,
        type: "column",
        // Precisa caber o rótulo das pontas ("mar-23" / "ago-26"), que numa
        // fonte de 20px não entra nos 32px que as outras famílias usam.
        marginBottom: 24 * ESCALA,
        // Folga lateral para as caixas de rótulo das pontas, que ficam
        // meio para fora da primeira e da última coluna.
        marginLeft: 16 * ESCALA,
        marginRight: 16 * ESCALA,
      },
      xAxis: {
        ...base.xAxis,
        categories: categorias,
        lineWidth: 0,
        tickWidth: 0,
        // Fixa os ticks nas duas pontas em vez de filtrar no formatter.
        // Com 40+ categorias o Highcharts afina os ticks sozinho, e aí
        // isFirst/isLast passam a apontar para os ticks que sobraram — nao
        // para a primeira e a ultima categoria. O resultado era um rotulo
        // do meio ("m38") no lugar de "ago-26".
        tickPositions: ultimo > 0 ? [0, ultimo] : [0],
        // Sem sobrescrever o tamanho: o base ja entrega a fonte na
        // escala certa (manual + automatica).
        labels: base.xAxis.labels,
      },
      yAxis: { ...base.yAxis, maxPadding: 0.16 },
      plotOptions: {
        ...base.plotOptions,
        column: {
          borderWidth: 0,
          groupPadding: 0.02,
          pointPadding: 0,
          dataLabels: {
            ...estiloRotulo(spec),
            formatter() {
              return formatarValor(this.y, opcoesValor);
            },
          },
        },
      },
      series: [
        {
          name: spec.dados?.series?.[0]?.nome || "Série",
          showInLegend: false,
          data: valores.map((valor, i) => {
            // Com `tamanhoBloco` a cor muda em degraus (um tom por ano,
            // como nas referências); sem ele, varia continuamente.
            const bloco = spec.extras?.tamanhoBloco;
            const cor = bloco
              ? rampa[Math.floor(i / bloco) % rampa.length]
              : interpolarCores(rampa, ultimo > 0 ? i / ultimo : 0);
            return {
              y: valor === null ? null : Number(valor),
              color: gradienteVertical(cor, escurecer(cor)),
              dataLabels: { enabled: i === 0 || i === ultimo },
            };
          }),
        },
      ],
    };
  },

  exemplo() {
    const meses = [];
    const valores = [];
    const rotulos = ["mar-23", ...Array.from({ length: 40 }, (_, i) => `m${i}`), "ago-26"];
    for (let i = 0; i < 42; i++) {
      meses.push(rotulos[i] || `m${i}`);
      valores.push(Number((6.4 + Math.sin(i / 4) * 0.7 + (i % 5) * 0.06).toFixed(2)));
    }
    valores[0] = 6.88;
    valores[valores.length - 1] = 6.78;

    return {
      modelo: "coluna-gradiente",
      titulo: [
        { texto: "Trigo", cor: "#FF4D4F" },
        { texto: " - Média Bolsa de Chicago ", cor: "#FFFFFF" },
        { texto: "(US$/bushel)", cor: "#FFFFFF", tamanho: 8 },
      ],
      subtitulo: [{ texto: "Média Mensal  -  1º Contrato", cor: "#3FA9F5" }],
      fonte: "Fonte: RURAL BUSINESS/CME Group",
      fundo: "#000000",
      fundoPlot: "nenhum",
      dados: {
        categorias: meses,
        series: [{ nome: "Trigo", valores }],
      },
      legenda: { visivel: false },
      eixo: { min: 4.8, max: null },
      valores: { decimais: 2, prefixo: "", sufixo: "", exibirEm: "extremos" },
      rotulo: { corTexto: "#000000", corFundo: "#FFFFFF", corBorda: "#000000" },
      // Um tom por ano: 12 meses por bloco, como na referência.
      extras: {
        rampa: ["#8FC7F0", "#F0F000", "#E03C3C", "#3DAA73"],
        tamanhoBloco: 12,
      },
    };
  },
};

export default modelo;
